import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Crown } from "@phosphor-icons/react";

/**
 * Public live counter — shows how many Founding Member spots remain.
 * Pulls from /api/founding/spots (no auth required, cached 5 min server-side).
 *
 * Variants:
 *   - "hero"   : large pill for landing page hero
 *   - "inline" : compact banner for /founding page above the tier cards
 */
export const FoundingSpotsCounter = ({ variant = "inline" }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/founding/spots`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return null;
  if (!data) return null;

  const { total_spots, claimed, remaining } = data;
  const pctClaimed = total_spots > 0 ? Math.min(100, (claimed / total_spots) * 100) : 0;
  // Pick urgency color based on remaining %
  const urgencyColor =
    remaining <= 10 ? "text-amber-300" :
    remaining <= 25 ? "text-emerald-300" :
    "text-emerald-400";

  if (variant === "hero") {
    return (
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 backdrop-blur"
        data-testid="founding-spots-hero"
      >
        <Crown weight="fill" className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-semibold text-emerald-300">
          {remaining} of {total_spots} Founding Member spots remaining
        </span>
      </div>
    );
  }

  // inline variant — for /founding page
  return (
    <div
      className="max-w-2xl mx-auto mb-8 p-4 rounded-sm bg-emerald-500/5 border border-emerald-500/30"
      data-testid="founding-spots-inline"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-emerald-300 font-mono flex items-center gap-1.5">
          <Crown weight="fill" className="w-3.5 h-3.5" />
          Founding Member Spots
        </span>
        <span className={`text-sm font-bold font-mono ${urgencyColor}`} data-testid="founding-spots-count">
          {remaining} of {total_spots} remaining
        </span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700"
          style={{ width: `${pctClaimed}%` }}
        />
      </div>
      <p className="text-xs text-zinc-500 mt-2">
        {claimed} {claimed === 1 ? "nonprofit has" : "nonprofits have"} already locked in their founding rate.
      </p>
    </div>
  );
};

export default FoundingSpotsCounter;
